import { useState } from 'react'
import { type Driver, type VehicleType, type DriverStatus } from '~/api/drivers'
import { useUsers, type User } from '~/api/users'
import styles from './DriverModal.module.css'

interface DriverModalProps {
  driver?: Driver | null
  isOpen: boolean
  onClose: () => void
  onSave: (data: DriverFormData) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  isSaving: boolean
}

export interface DriverFormData {
  userId: string
  licenseNumber: string
  vehicleType: VehicleType
  vehiclePlate: string
  maxCapacityKg: number
  status?: DriverStatus
}

const VEHICLE_TYPES: VehicleType[] = ['MOTORCYCLE', 'CAR', 'VAN', 'TRUCK']
const DRIVER_STATUSES: DriverStatus[] = ['AVAILABLE', 'ON_DELIVERY', 'OFFLINE', 'ON_BREAK']

const INITIAL_FORM: DriverFormData = {
  userId: '',
  licenseNumber: '',
  vehicleType: 'MOTORCYCLE',
  vehiclePlate: '',
  maxCapacityKg: 50,
}

function formFromDriver(driver?: Driver | null): DriverFormData {
  if (!driver) return INITIAL_FORM

  return {
    userId: driver.userId,
    licenseNumber: driver.licenseNumber,
    vehicleType: driver.vehicleType as VehicleType,
    vehiclePlate: driver.vehiclePlate,
    maxCapacityKg: driver.maxCapacityKg,
    status: driver.status,
  }
}

export function DriverModal(props: DriverModalProps) {
  if (!props.isOpen) return null

  return <DriverModalContent key={props.driver?.id ?? 'new-driver'} {...props} />
}

function DriverModalContent({ driver, onClose, onSave, onDelete, isSaving }: DriverModalProps) {
  const [form, setForm] = useState<DriverFormData>(() => formFromDriver(driver))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { data: users } = useUsers()

  const isEditing = !!driver

  // Filter users who have driver role but aren't already drivers (for create mode)
  const availableUsers = users?.filter((u: User) => 
    u.roles.includes('driver') && (isEditing ? u.localId === driver.userId : true)
  ) ?? []

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!isEditing && !form.userId) newErrors.userId = 'User is required'
    if (!form.licenseNumber.trim()) newErrors.licenseNumber = 'License number is required'
    if (!form.vehiclePlate.trim()) newErrors.vehiclePlate = 'Vehicle plate is required'
    if (form.maxCapacityKg <= 0) newErrors.maxCapacityKg = 'Capacity must be greater than 0'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    await onSave(form)
  }

  const handleDelete = async () => {
    if (!driver || !onDelete) return
    await onDelete(driver.id)
    onClose()
  }

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{isEditing ? 'EDIT DRIVER' : 'CREATE DRIVER'}</h2>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {!isEditing && (
            <div className={styles.formGroup}>
              <label className={styles.label}>User</label>
              <select
                className={styles.select}
                value={form.userId}
                onChange={e => setForm(prev => ({ ...prev, userId: e.target.value }))}
              >
                <option value="">Select a user...</option>
                {availableUsers.map((user: User) => (
                  <option key={user.id} value={user.localId}>
                    {user.firstName} {user.lastName} ({user.username})
                  </option>
                ))}
              </select>
              {errors.userId && <span className={styles.error}>{errors.userId}</span>}
              {availableUsers.length === 0 && (
                <span className={styles.error}>No users with driver role available</span>
              )}
            </div>
          )}

          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>License Number</label>
              <input
                type="text"
                className={styles.input}
                value={form.licenseNumber}
                onChange={e => setForm(prev => ({ ...prev, licenseNumber: e.target.value }))}
                placeholder="e.g., DL-12345678"
              />
              {errors.licenseNumber && <span className={styles.error}>{errors.licenseNumber}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Vehicle Type</label>
              <select
                className={styles.select}
                value={form.vehicleType}
                onChange={e => setForm(prev => ({ ...prev, vehicleType: e.target.value as VehicleType }))}
              >
                {VEHICLE_TYPES.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Vehicle Plate</label>
              <input
                type="text"
                className={styles.input}
                value={form.vehiclePlate}
                onChange={e => setForm(prev => ({ ...prev, vehiclePlate: e.target.value }))}
                placeholder="e.g., AB 1234 CD"
              />
              {errors.vehiclePlate && <span className={styles.error}>{errors.vehiclePlate}</span>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Max Capacity (kg)</label>
              <input
                type="number"
                className={styles.input}
                value={form.maxCapacityKg}
                onChange={e => setForm(prev => ({ ...prev, maxCapacityKg: Number(e.target.value) }))}
                min={1}
              />
              {errors.maxCapacityKg && <span className={styles.error}>{errors.maxCapacityKg}</span>}
            </div>

            {isEditing && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Status</label>
                <select
                  className={styles.select}
                  value={form.status}
                  onChange={e => setForm(prev => ({ ...prev, status: e.target.value as DriverStatus }))}
                >
                  {DRIVER_STATUSES.map(status => (
                    <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className={styles.actions}>
            {isEditing && onDelete && (
              showDeleteConfirm ? (
                <>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={handleDelete}
                    disabled={isSaving}
                  >
                    CONFIRM DELETE
                  </button>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    CANCEL
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  DELETE
                </button>
              )
            )}
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              CANCEL
            </button>
            <button type="submit" className={styles.saveButton} disabled={isSaving}>
              {isSaving ? 'SAVING...' : isEditing ? 'UPDATE' : 'CREATE'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
